import {
  ACTIVE_SORT_OPTIONS,
  ARCHIVED_VALID_SORT_OPTIONS,
  GroupByOption,
  NetworkPortfolioManagementResourceView,
  SELECTABLE_SORT_OPTIONS,
  SortByOption,
  SortGroupState,
  defaultSortGroupState,
} from "@app/network-portfolio-management/interfaces";

// Active and Hidden expose the identical option set and must never diverge — they share one bucket
// so "switching carries the selection over unchanged" needs no special-case code. Archived offers a
// different set (Last Run On, Archive Date, and Alphabetical; no grouping) and gets its own bucket so
// a fallback there can never clobber Active/Hidden's remembered selection. Archive Date is archived-
// only, so it can never leak into the Active/Hidden bucket — see isValidForView.
export const SORT_GROUP_SESSION_KEY_ACTIVE_HIDDEN = "npm_sort_group_active_hidden";
export const SORT_GROUP_SESSION_KEY_ARCHIVED = "npm_sort_group_archived";

const bucketKeyFor = (view: NetworkPortfolioManagementResourceView): string =>
  view === NetworkPortfolioManagementResourceView.Archived
    ? SORT_GROUP_SESSION_KEY_ARCHIVED
    : SORT_GROUP_SESSION_KEY_ACTIVE_HIDDEN;

export function isValidForView(state: SortGroupState, view: NetworkPortfolioManagementResourceView): boolean {
  if (view !== NetworkPortfolioManagementResourceView.Archived) {
    // Active/Hidden accept any Group By; the sort still has to be one they offer (Archive Date is not).
    return ACTIVE_SORT_OPTIONS.includes(state.sortBy);
  }
  return state.groupBy === GroupByOption.None && ARCHIVED_VALID_SORT_OPTIONS.includes(state.sortBy);
}

export function readStoredSortGroupState(view: NetworkPortfolioManagementResourceView): SortGroupState | null {
  try {
    const raw = sessionStorage.getItem(bucketKeyFor(view));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SortGroupState>;
    if (!SELECTABLE_SORT_OPTIONS.includes(parsed.sortBy as SortByOption)) return null;
    if (!Object.values(GroupByOption).includes(parsed.groupBy as GroupByOption)) return null;
    return { groupBy: parsed.groupBy as GroupByOption, sortBy: parsed.sortBy as SortByOption };
  } catch {
    return null;
  }
}

export function writeStoredSortGroupState(view: NetworkPortfolioManagementResourceView, state: SortGroupState): void {
  if (!isValidForView(state, view)) return;
  try {
    sessionStorage.setItem(bucketKeyFor(view), JSON.stringify(state));
  } catch {
    // sessionStorage unavailable — fail silently
  }
}

/**
 * Resolves the SortGroupState to show for `view`. Reads only that view's own bucket — never the
 * outgoing view's in-memory value — so each view remembers only its own last selection, in both
 * directions: switching never carries a selection across views, valid or not.
 */
export function resolveSortGroupStateForView(view: NetworkPortfolioManagementResourceView): SortGroupState {
  const stored = readStoredSortGroupState(view);
  return stored && isValidForView(stored, view) ? stored : defaultSortGroupState();
}
